/**
 * ExportDialog — PNG / JPEG / SVG / PDF / JSON export with per-format options.
 * Pulls pixels/markup from the CanvasCore handle (`api.exportImage`) and
 * triggers a browser download. PDF is assembled client-side with jsPDF.
 */
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { jsPDF } from 'jspdf';
import { toast } from '../../../utils/toast';
import { X, Download, FileImage, FileCode2, FileText, Braces } from 'lucide-react';
import { T } from '../../../components/em/ui';

const FORMATS = [
  { id: 'png', label: 'PNG', Icon: FileImage, note: 'Lossless image, optional transparency' },
  { id: 'jpeg', label: 'JPEG', Icon: FileImage, note: 'Smaller file, quality slider' },
  { id: 'svg', label: 'SVG', Icon: FileCode2, note: 'Scalable vector' },
  { id: 'pdf', label: 'PDF', Icon: FileText, note: 'Print-ready document' },
  { id: 'json', label: 'JSON', Icon: Braces, note: 'Raw data for backup / re-import' },
];

const triggerDownload = (href, filename) => {
  const a = document.createElement('a');
  a.href = href; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
};

export default function ExportDialog({ api, name, onClose }) {
  const [format, setFormat] = useState('png');
  const [quality, setQuality] = useState(0.92);
  const [transparent, setTransparent] = useState(false);
  const [busy, setBusy] = useState(false);

  const safeName = (name || 'canvas').trim().replace(/[^\w-]+/g, '_').slice(0, 60) || 'canvas';
  const supportsTransparent = format === 'png' || format === 'svg';

  const doExport = async () => {
    setBusy(true);
    try {
      if (format === 'svg') {
        const svg = await api.exportImage({ format: 'svg' });
        triggerDownload(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`, `${safeName}.svg`);
      } else if (format === 'json') {
        const json = await api.exportImage({ format: 'json' });
        triggerDownload(`data:application/json;charset=utf-8,${encodeURIComponent(json)}`, `${safeName}.json`);
      } else if (format === 'pdf') {
        const dataURL = await api.exportImage({ format: 'png', multiplier: 2, includeBackground: true });
        const img = new Image();
        await new Promise((r) => { img.onload = r; img.src = dataURL; });
        const landscape = img.width >= img.height;
        const pdf = new jsPDF({ orientation: landscape ? 'l' : 'p', unit: 'pt', format: [img.width, img.height] });
        pdf.addImage(dataURL, 'PNG', 0, 0, img.width, img.height);
        pdf.save(`${safeName}.pdf`);
      } else {
        const dataURL = await api.exportImage({ format, quality, includeBackground: !transparent, transparent });
        triggerDownload(dataURL, `${safeName}.${format}`);
      }
      toast.success(`Exported ${format.toUpperCase()}`);
      onClose();
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div onMouseDown={onClose} style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(10,12,20,0.5)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onMouseDown={(e) => e.stopPropagation()} style={{ width: 420, maxWidth: '92vw', background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 18, boxShadow: '0 30px 80px -20px rgba(0,0,0,0.5)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', borderBottom: `1px solid ${T.border}` }}>
          <span style={{ font: `700 16px ${T.font}`, color: T.fg1 }}>Export canvas</span>
          <button type="button" onClick={onClose} style={{ border: 0, background: 'transparent', cursor: 'pointer', color: T.fg5, display: 'flex' }}><X size={18} /></button>
        </div>

        <div style={{ padding: 18 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
            {FORMATS.map((f) => {
              const on = format === f.id;
              return (
                <button key={f.id} type="button" onClick={() => setFormat(f.id)}
                  style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start', textAlign: 'left', padding: '11px 13px', borderRadius: 12, cursor: 'pointer', border: `1.5px solid ${on ? T.orange : T.border}`, background: on ? T.orange50 : 'transparent' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, font: `700 13px ${T.font}`, color: on ? T.orange : T.fg1 }}>
                    <f.Icon size={16} /> {f.label}
                  </span>
                  <span style={{ font: `400 10.5px/1.3 ${T.font}`, color: T.fg7 }}>{f.note}</span>
                </button>
              );
            })}
          </div>

          {format === 'jpeg' && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <span style={{ font: `500 12px ${T.font}`, color: T.fg5 }}>Quality</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="range" min={0.1} max={1} step={0.05} value={quality} onChange={(e) => setQuality(Number(e.target.value))} style={{ width: 140 }} />
                <span style={{ font: `600 12px ${T.font}`, color: T.fg2, width: 34 }}>{Math.round(quality * 100)}%</span>
              </div>
            </div>
          )}

          {supportsTransparent && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={transparent} onChange={(e) => setTransparent(e.target.checked)} />
              <span style={{ font: `500 12px ${T.font}`, color: T.fg3 }}>Transparent background</span>
            </label>
          )}

          <button type="button" onClick={doExport} disabled={busy}
            style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 42, borderRadius: 11, border: 0, cursor: 'pointer', background: T.gradient, color: '#fff', font: `700 14px ${T.font}`, boxShadow: '0 6px 16px -6px rgba(245,75,1,0.6)' }}>
            <Download size={17} /> {busy ? 'Exporting…' : `Export ${format.toUpperCase()}`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
